import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { Alert, Box, CircularProgress, Stack, Typography, Chip } from '@mui/material';
import { getSyncHealth } from './api';
import {
    aggregate, buildRanking, sortBy, faultBreakdown, clientFaultMix, countVolumes,
} from './aggregation';
import { MIN_RUNS, MIN_NIGHTS, RECENT, TOP_N } from './format';
import OverviewRow from './OverviewRow';
import TldrSection from './TldrSection';
import DefinitionsSection from './DefinitionsSection';
import WeightingSection from './WeightingSection';
import FaultRulesSection from './FaultRulesSection';
import FaultBreakdownSection from './FaultBreakdownSection';
import TrendSection from './TrendSection';
import RankingsSection from './RankingsSection';
import FilterDialog, { FilterToggle } from './FilterDialog';
import './SyncHealth.css';

const initialState = {
    excludedClients: new Set(),
    excludedProviders: new Set(),
    weighting: 'equal',
    faultMode: 'all',
};

// Set helpers — useReducer demands new references for React to re-render.
const toggleInSet = (set, idx) => {
    const next = new Set(set);
    if (next.has(idx)) next.delete(idx); else next.add(idx);
    return next;
};

function reducer(state, action) {
    switch (action.type) {
        case 'toggle-client':
            return { ...state, excludedClients: toggleInSet(state.excludedClients, action.i) };
        case 'toggle-provider':
            return { ...state, excludedProviders: toggleInSet(state.excludedProviders, action.i) };
        case 'set-all-clients':
            return {
                ...state,
                excludedClients: action.exclude
                    ? new Set(state.allClientIdx)
                    : new Set(),
            };
        case 'set-all-providers':
            return {
                ...state,
                excludedProviders: action.exclude
                    ? new Set(state.allProviderIdx)
                    : new Set(),
            };
        case 'init-counts':
            return {
                ...state,
                allClientIdx: action.clients,
                allProviderIdx: action.providers,
            };
        case 'weighting':
            return { ...state, weighting: action.value };
        case 'fault-mode':
            return { ...state, faultMode: action.value };
        case 'reset':
            return { ...initialState, allClientIdx: state.allClientIdx, allProviderIdx: state.allProviderIdx };
        default:
            return state;
    }
}

const SyncHealth = () => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [state, dispatch] = useReducer(reducer, initialState);
    const [filterOpen, setFilterOpen] = useState(false);

    useEffect(() => {
        getSyncHealth().then((d) => {
            setData(d);
            dispatch({
                type: 'init-counts',
                clients: d.clients.map((_, i) => i),
                providers: d.providers.map((_, i) => i),
            });
        }).catch((e) => setError(e.message));
    }, []);

    // Memoize the aggregation pipeline so it only re-runs when state or data changes.
    const agg = useMemo(() => {
        if (!data) return null;
        const a = aggregate(data, state);
        const { cVol, pVol } = countVolumes(data, state);
        const fb14 = faultBreakdown(data, state, RECENT);
        const fbFull = faultBreakdown(data, state, data.runs.length);
        const faultMix = clientFaultMix(data, state);

        const clientRows = buildRanking(a.perClient, data.clients, RECENT);
        for (const r of clientRows) r.volume = cVol[r.idx] || 0;
        const providerRows = buildRanking(a.perProvider, data.providers, RECENT);
        for (const r of providerRows) r.volume = pVol[r.idx] || 0;

        const clientsRanked = clientRows.filter((r) => r.volume >= MIN_RUNS && r.nightsRan >= MIN_NIGHTS);
        const providersRanked = providerRows.filter((r) => r.volume >= MIN_RUNS && r.nightsRan >= MIN_NIGHTS);

        const worstClients = sortBy(clientsRanked, 'last14_clean', true).slice(0, TOP_N);
        const bestClients = sortBy(clientsRanked, 'last14_clean', false).slice(0, TOP_N);
        const regressorClients = sortBy(clientsRanked.filter((r) => r.delta_clean < 0), 'delta_clean', true).slice(0, TOP_N);
        const improverClients = sortBy(clientsRanked.filter((r) => r.delta_clean > 0), 'delta_clean', false).slice(0, TOP_N);
        const worstProviders = sortBy(providersRanked, 'last14_clean', true).slice(0, TOP_N);
        const bestProviders = sortBy(providersRanked, 'last14_clean', false).slice(0, TOP_N);
        const regressorProviders = sortBy(providersRanked.filter((r) => r.delta_clean < 0), 'delta_clean', true).slice(0, TOP_N);
        const improverProviders = sortBy(providersRanked.filter((r) => r.delta_clean > 0), 'delta_clean', false).slice(0, TOP_N);

        return {
            ...a,
            fb14, fbFull, faultMix,
            worstClients, bestClients, regressorClients, improverClients,
            worstProviders, bestProviders, regressorProviders, improverProviders,
            clientsRankedCount: clientsRanked.length,
            clientsTotalCount: clientRows.length,
            providersRankedCount: providersRanked.length,
            providersTotalCount: providerRows.length,
            watchClients: worstClients.slice(0, 3).map((r) => r.name),
            watchProviders: worstProviders.slice(0, 3).map((r) => r.name),
        };
    }, [data, state]);

    if (error) {
        return <Alert severity="error" sx={{ mb: 2 }}>Failed to load sync health: {error}</Alert>;
    }
    if (!data || !agg) {
        return (
            <Stack alignItems="center" spacing={1.5} sx={{ py: 8 }}>
                <CircularProgress size={32} sx={{ color: 'var(--color-brand-blue, #004df2)' }} />
                <Typography sx={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                    Loading sync health…
                </Typography>
            </Stack>
        );
    }

    const totalExcl = state.excludedClients.size + state.excludedProviders.size;
    const filterParts = [];
    if (state.weighting !== 'equal') filterParts.push(`weighting clients by ${state.weighting}`);
    if (state.faultMode !== 'all') filterParts.push(`counting only ${state.faultMode}-fault failures`);
    if (state.excludedClients.size) filterParts.push(`excluded ${state.excludedClients.size} client${state.excludedClients.size === 1 ? '' : 's'}`);
    if (state.excludedProviders.size) filterParts.push(`excluded ${state.excludedProviders.size} provider${state.excludedProviders.size === 1 ? '' : 's'}`);

    return (
        <Box className="sync-health-container">
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                <Box>
                    <Typography sx={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text-primary)' }}>
                        Sync Health
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                        {data.dates[0]} → {data.dates[data.dates.length - 1]} ({data.dates.length} nights).
                    </Typography>
                    {filterParts.length > 0 && (
                        <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap' }}>
                            {filterParts.map((p, i) => <Chip key={i} label={p} size="small" sx={{ background: '#fdf2ee', color: '#e76f51', fontSize: 11 }} />)}
                        </Stack>
                    )}
                </Box>
                <FilterToggle open={filterOpen} onClick={() => setFilterOpen(!filterOpen)} excludedCount={totalExcl + (state.weighting !== 'equal' ? 1 : 0) + (state.faultMode !== 'all' ? 1 : 0)} />
            </Stack>

            <OverviewRow data={data} agg={agg} state={state} />
            <TldrSection data={data} agg={agg} state={state} />
            <TrendSection data={data} agg={agg} />
            <RankingsSection data={data} agg={agg} />
            <FaultBreakdownSection data={data} agg={agg} />
            <DefinitionsSection />
            <WeightingSection data={data} />
            <FaultRulesSection data={data} />

            <Box sx={{ fontSize: 12, color: '#888', borderTop: '1px solid #eee', pt: 2, mt: 2, lineHeight: 1.6 }}>
                <b>Units:</b> A <i>provider run</i> = all jobs for a single (client, provider) pair on a single night;
                succeeds iff every job in it succeeded. A <i>client night</i> is clean iff every one of that client's
                provider-runs that night succeeded. A <i>system night</i> is clean iff every run across every client and
                provider succeeded. <b>Per-job success</b> is ported from <code>sync_status_email.py::_derive_job_status</code>
                — a job is a failure iff final status is <code>Failed</code>, or it had an <code>Error</code> event with no
                completion. Jobs whose <code>job_type</code> doesn't classify into a pipeline phase are excluded before the
                run rollup. Rolling window = 7 days, min_periods = 3. Ranking filter: minimum {MIN_RUNS} runs + {MIN_NIGHTS} nights.
            </Box>

            <FilterDialog
                open={filterOpen}
                onClose={() => setFilterOpen(false)}
                data={data}
                state={state}
                dispatch={dispatch}
            />
        </Box>
    );
};

export default SyncHealth;
